import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Button, 
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  EyeIcon, 
  ArrowDownTrayIcon, 
  ShareIcon,
  CheckBadgeIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

const CertificationCard = ({ certificate, onView, onDownload }) => {
  const issueDate = certificate.issueDate 
    ? new Date(certificate.issueDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : 'N/A';

  const handleShare = (e) => {
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: `Certification: ${certificate.certificateName || 'Achievement'}`,
        text: `I just earned a certification in ${certificate.programName || 'this program'}!`,
        url: certificate.verification_url || window.location.href,
      }).catch(console.error);
    } else {
      // Fallback: Copy to clipboard
      navigator.clipboard.writeText(certificate.verification_url || window.location.href);
      alert('Verification link copied to clipboard!');
    }
  };

  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '20px',
        border: '1px solid #E2E8F0',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 12px 24px rgba(132, 63, 239, 0.12)',
          borderColor: 'rgba(132, 63, 239, 0.3)',
        }
      }}
    >
      <Box sx={{ 
        height: '140px', 
        bgcolor: '#F8FAFC', 
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        borderBottom: '1px solid #F1F5F9'
      }}>
        {certificate.certificate_image_url ? (
          <img 
            src={certificate.certificate_image_url} 
            alt="Certificate Preview" 
            style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.8 }}
          />
        ) : (
          <AcademicCapIcon className="h-16 w-16 text-[#843FEF]/20" />
        )}
        
        <Box sx={{ 
          position: 'absolute', 
          top: 12, 
          right: 12,
          display: 'flex',
          gap: 1
        }}>
          <Chip 
            label="Verified" 
            size="small"
            icon={<CheckBadgeIcon className="h-3 w-3" style={{ color: '#059669' }} />}
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.9)', 
              backdropFilter: 'blur(4px)',
              color: '#059669', 
              fontWeight: 800,
              fontSize: '0.65rem',
              height: '24px',
              border: '1px solid #D1FAE5'
            }} 
          />
        </Box>
      </Box>

      <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography 
          variant="caption" 
          sx={{ 
            color: '#843FEF', 
            fontWeight: 800, 
            textTransform: 'uppercase', 
            letterSpacing: '0.05em',
            mb: 0.5,
            display: 'block'
          }}
        >
          {certificate.programName || certificate.courseName || 'Program'}
        </Typography>
        
        <Typography 
          variant="h6" 
          sx={{ 
            fontSize: '1rem', 
            fontWeight: 800, 
            color: '#0F172A', 
            mb: 1.5,
            lineHeight: 1.3,
            minHeight: '2.6em',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {certificate.certificateName || 'Certificate of Achievement'}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          <InfoRow label="College" value={certificate.collegeName} />
          <InfoRow label="ID" value={certificate.certificateId || certificate._id} />
          <InfoRow label="Issued" value={issueDate} />
          {certificate.grade && <InfoRow label="Grade" value={certificate.grade} isBadge />}
        </Box>

        <Box sx={{ mt: 'auto', display: 'flex', gap: 1, pt: 1.5 }}>
          <Button 
            variant="contained" 
            size="small"
            startIcon={<EyeIcon className="h-4 w-4" />}
            onClick={() => onView(certificate)}
            sx={{ 
              flex: 1,
              bgcolor: '#843FEF',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8rem',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#7035CC', boxShadow: 'none' }
            }}
          >
            View
          </Button>
          
          <Tooltip title="Download">
            <IconButton 
              size="small"
              onClick={(e) => { e.stopPropagation(); onDownload(certificate); }}
              sx={{ 
                bgcolor: '#F1F5F9', 
                color: '#64748B',
                borderRadius: '10px',
                '&:hover': { bgcolor: '#E2E8F0', color: '#0F172A' }
              }}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Share Achievement">
            <IconButton 
              size="small"
              onClick={handleShare}
              sx={{ 
                bgcolor: '#F1F5F9', 
                color: '#64748B',
                borderRadius: '10px',
                '&:hover': { bgcolor: '#E2E8F0', color: '#0F172A' }
              }}
            >
              <ShareIcon className="h-4 w-4" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

const InfoRow = ({ label, value, isBadge }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>{label}</Typography>
    {isBadge ? (
      <Chip 
        label={value} 
        size="small" 
        sx={{ 
          height: '20px', 
          fontSize: '0.65rem', 
          fontWeight: 800, 
          bgcolor: '#EFF6FF', 
          color: '#3B82F6',
          borderRadius: '6px'
        }} 
      />
    ) : (
      <Typography 
        variant="caption" 
        sx={{ 
          color: '#334155', 
          fontWeight: 700,
          maxWidth: '120px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {value || 'N/A'}
      </Typography>
    )}
  </Box>
);

export default CertificationCard;
